// apps/web/src/features/admin/form-config/PublishModal.tsx
import { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import type { ValidationError, JobTypeVersionContent, VersionDiff } from '@capture/shared';
import { computeVersionDiff } from '@capture/shared';
import './PublishModal.css';

interface PublishModalBaseProps {
  jobTypeName?: string;
  serverErrors?: ValidationError[];
  onPublish: (body: { publishNote: string; typedConfirmation: string }) => void;
  onCancel: () => void;
  /**
   * Called when the operator clicks a server error that carries
   * `linkTarget`. The consumer (FormConfigPage) closes the modal,
   * switches the form config tab to `target.tab`, and scrolls the
   * named field into view. R-71 / WU-1.1.
   */
  onErrorClick?: (target: NonNullable<ValidationError['linkTarget']>) => void;
}

interface DiffProps extends PublishModalBaseProps {
  diff: VersionDiff;
  before?: never;
  after?: never;
}

interface BeforeAfterProps extends PublishModalBaseProps {
  before: JobTypeVersionContent;
  after: JobTypeVersionContent;
  diff?: never;
}

export type PublishModalProps = DiffProps | BeforeAfterProps;

// P2-1: pass-1 shipped this as a plain <div role="dialog"> with no positioning
// CSS, so it rendered inline at the bottom of FormConfigPage. Wrap in Radix
// Dialog primitives for portal + backdrop + focus-trap + Escape-to-close —
// matches the established admin pattern (see PromoteToCanonicalDialog).
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  zIndex: 200,
};

const contentStyle: React.CSSProperties = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  background: 'var(--color-bg-primary, #0e0e0e)',
  border: '1px solid var(--color-border, #2a2a2a)',
  borderRadius: 6,
  padding: 24,
  zIndex: 201,
  minWidth: 560,
  maxWidth: 720,
  maxHeight: '85vh',
  overflowY: 'auto',
};

export function PublishModal(props: PublishModalProps) {
  const { jobTypeName = 'Form', serverErrors, onPublish, onCancel, onErrorClick } = props;
  const [publishNote, setPublishNote] = useState('');
  const [typedConfirmation, setTypedConfirmation] = useState('');

  const diff = useMemo<VersionDiff>(() => {
    if (props.diff) return props.diff;
    return computeVersionDiff(props.before, props.after);
  }, [props.diff, props.before, props.after]);

  const canPublish = publishNote.trim().length > 0 && typedConfirmation === jobTypeName;

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <Dialog.Portal>
        <Dialog.Overlay style={overlayStyle} />
        <Dialog.Content style={contentStyle} className="publish-modal" aria-describedby={undefined}>
          <Dialog.Title asChild>
            <h2>Publish this draft</h2>
          </Dialog.Title>

          <section className="publish-modal__diff">
            <h3>Changes</h3>
            {diff.fieldsAdded.length > 0 && <p><strong>Added:</strong> {diff.fieldsAdded.join(', ')}</p>}
            {diff.fieldsRemoved.length > 0 && <p><strong>Removed:</strong> {diff.fieldsRemoved.join(', ')}</p>}
            {diff.fieldsChanged.length > 0 && <p><strong>Changed:</strong> {diff.fieldsChanged.map((f) => f.name).join(', ')}</p>}
            {diff.pipelineChanged && <p>Pipeline stages changed</p>}
            {diff.headerMappingChanged && <p>Header mapping changed</p>}
            {diff.rulesChanged && (
              <div>
                <p><strong>Rules:</strong></p>
                {diff.rulesDiff && diff.rulesDiff.length > 0 ? (
                  <ul>
                    {diff.rulesDiff.map((rd) => (
                      <li key={rd.ruleId}>
                        {rd.type === 'added' && <span className="publish-modal__diff-added">+ {rd.ruleName}</span>}
                        {rd.type === 'removed' && <span className="publish-modal__diff-removed">− {rd.ruleName}</span>}
                        {rd.type === 'changed' && <span className="publish-modal__diff-changed">~ {rd.ruleName}{rd.details ? ` (${rd.details})` : ''}</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>Rules changed</p>
                )}
              </div>
            )}
            {diff.exportChanged && diff.exportDiff ? (
              <div>
                <p><strong>Export Changes:</strong></p>
                <ul>
                  {diff.exportDiff.destinationsAdded.map((name) => (
                    <li key={`export-added-${name}`}>
                      <span className="publish-modal__diff-added">+ {name}</span>
                    </li>
                  ))}
                  {diff.exportDiff.destinationsRemoved.map((name) => (
                    <li key={`export-removed-${name}`}>
                      <span className="publish-modal__diff-removed">− {name}</span>
                    </li>
                  ))}
                  {diff.exportDiff.destinationsChanged.map((d) => (
                    <li key={`export-changed-${d.name}`}>
                      <span className="publish-modal__diff-changed">~ {d.name} ({d.changes.join(', ')})</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : diff.exportChanged ? (
              <p>Export destinations changed</p>
            ) : null}
            {diff.summary.length > 0 && (
              <ul>
                {diff.summary.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
          </section>

          {serverErrors && serverErrors.length > 0 && (
            <section className="publish-modal__errors">
              <h3>Cannot publish — fix these first</h3>
              <ul>
                {serverErrors.map((e, i) => (
                  <li key={i}>
                    {e.linkTarget?.tab && onErrorClick ? (
                      <button
                        type="button"
                        className="publish-modal__error-link"
                        onClick={() => onErrorClick(e.linkTarget!)}
                      >
                        <code>{e.code}</code>: {e.message}
                        <span className="publish-modal__error-link-hint"> — jump to field</span>
                      </button>
                    ) : (
                      <><code>{e.code}</code>: {e.message}</>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <label>
            Publish note
            <textarea value={publishNote} onChange={(e) => setPublishNote(e.target.value)} rows={3} />
          </label>

          <label>
            Type <strong>{jobTypeName}</strong> to confirm
            <input value={typedConfirmation} onChange={(e) => setTypedConfirmation(e.target.value)} />
          </label>

          <div className="publish-modal__actions">
            <button type="button" onClick={onCancel}>Cancel</button>
            <button type="button" disabled={!canPublish} onClick={() => onPublish({ publishNote: publishNote.trim(), typedConfirmation })}>Publish</button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
